import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Minus } from 'lucide-react';
import type { ProductStockData, UpdateStockData } from '@/services/branchs';

interface AdjustStockTabProps {
  stockData: ProductStockData | null | undefined;
  adjustmentType: 'add' | 'remove';
  adjustmentQuantity: string;
  onTypeChange: (type: 'add' | 'remove') => void;
  onQuantityChange: (value: string) => void;
  onSave: (payload: UpdateStockData) => void;
  isSaving?: boolean;
}

export function AdjustStockTab({
  stockData,
  adjustmentType,
  adjustmentQuantity,
  onTypeChange,
  onQuantityChange,
  onSave,
  isSaving = false,
}: AdjustStockTabProps) {
  const newStock =
    adjustmentType === 'add'
      ? (stockData?.quantity || 0) + (parseInt(adjustmentQuantity) || 0)
      : (stockData?.quantity || 0) - (parseInt(adjustmentQuantity) || 0);

  const handleSave = () => {
    onSave({
      quantity: newStock,
      price: stockData?.price || '0.00',
      sellPrice: stockData?.sellPrice || '0.00',
      isActive: stockData?.isActive ?? true,
      branchId: stockData?.branch?.id || '',
      productId: stockData?.product?.id || '',
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Adjust Stock Level</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label className="text-muted-foreground">Current Stock</Label>
            <div className="text-2xl font-semibold">
              {stockData?.quantity || 0} units
            </div>
          </div>

          <div className="space-y-2">
            <Label>Adjustment Type</Label>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant={adjustmentType === 'add' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => onTypeChange('add')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stock
              </Button>
              <Button
                type="button"
                variant={
                  adjustmentType === 'remove' ? 'destructive' : 'outline'
                }
                className="flex-1"
                onClick={() => onTypeChange('remove')}
              >
                <Minus className="h-4 w-4 mr-2" />
                Remove Stock
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="quantity">
              {adjustmentType === 'add'
                ? 'Quantity to Add'
                : 'Quantity to Remove'}
            </Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              value={adjustmentQuantity}
              onChange={(e) => onQuantityChange(e.target.value)}
              placeholder={`Enter ${adjustmentType === 'add' ? 'quantity to add' : 'quantity to remove'}`}
              className="mt-1"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-md">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Current Stock:</span>
              <span>{stockData?.quantity || 0} units</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-sm font-medium">
                {adjustmentType === 'add' ? 'Adding' : 'Removing'}:
              </span>
              <span
                className={
                  adjustmentType === 'add' ? 'text-green-600' : 'text-red-600'
                }
              >
                {adjustmentType === 'add' ? '+' : '-'}
                {parseInt(adjustmentQuantity) || 0} units
              </span>
            </div>
            <div className="h-px bg-border my-2" />
            <div className="flex justify-between font-semibold">
              <span>New Stock:</span>
              <span className={newStock < 0 ? 'text-red-600' : ''}>
                {newStock < 0 ? 'Invalid' : newStock} units
              </span>
            </div>
          </div>

          <Button
            onClick={handleSave}
            className="w-full"
            disabled={
              isSaving ||
              !adjustmentQuantity ||
              parseInt(adjustmentQuantity) <= 0 ||
              newStock < 0
            }
          >
            {isSaving ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {adjustmentType === 'add' ? 'Adding...' : 'Removing...'}
              </>
            ) : adjustmentType === 'add' ? (
              'Add to Stock'
            ) : (
              'Remove from Stock'
            )}
          </Button>

          {newStock < 0 && (
            <p className="text-sm text-red-600 text-center">
              Cannot remove more items than are in stock.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
